import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation } from "@/components/location-provider";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function SidebarAlerts({ collapsed = false }: { collapsed?: boolean }) {
  const [count, setCount] = useState(0);
  const [event, setEvent] = useState("");
  const [severity, setSeverity] = useState<"low" | "moderate" | "severe">("low");
  const { current: location } = useLocation();

  useEffect(() => {
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => {
        const alerts = d.weather?.alerts || [];
        setCount(alerts.length);
        if (alerts.length) {
          const a = alerts[0];
          const generic = ["weather", "alert", "warning", "watch", "advisory"];
          const title = generic.includes(a.event.toLowerCase())
            ? a.description.split(/[.\n]/)[0].trim()
            : a.event;
          setEvent(title);

          const fullText = [
            a.event || "",
            a.description || "",
            ...(a.tags || []),
          ]
            .join(" ")
            .toLowerCase();

          if (
            fullText.includes("severe") ||
            fullText.includes("extreme") ||
            fullText.includes("emergency") ||
            fullText.includes("catastrophic") ||
            fullText.includes("tornado") ||
            fullText.includes("hurricane") ||
            (fullText.includes("warning") && !fullText.includes("frost warning") && !fullText.includes("fog warning")) ||
            /\b\d{2,3}\s*(?:to|-)\s*\d{2,3}\s*mm\b/.test(fullText) ||
            /rainfall.*\d{2,}/.test(fullText)
          ) {
            setSeverity("severe");
          } else if (
            fullText.includes("watch") ||
            fullText.includes("moderate") ||
            fullText.includes("advisory")
          ) {
            setSeverity("moderate");
          } else {
            setSeverity("low");
          }
        }
      })
      .catch(() => setCount(0));
  }, [location.lat, location.lon]);

  if (!count) return null;

  if (collapsed) {
    return (
      <Link
        to="/alerts"
        className="flex items-center justify-center mx-2 mt-2 h-10 w-10 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive/15 transition-all"
      >
        <span className="relative">
          <AlertTriangle className="h-4 w-4" />
          <span className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[8px] font-bold text-destructive-foreground">
            {count}
          </span>
        </span>
      </Link>
    );
  }

  const severityStyles = {
    low: "border-transparent bg-gradient-to-r from-amber-500/5 to-transparent",
    moderate: "border-transparent bg-gradient-to-r from-orange-500/8 to-transparent",
    severe: "border-transparent bg-gradient-to-r from-destructive/10 to-transparent",
  };

  const severityDots = {
    low: "bg-amber-400",
    moderate: "bg-orange-400",
    severe: "bg-destructive",
  };

  return (
    <Link
      to="/alerts"
      className={cn(
        "block mx-2 mt-3 rounded-xl border border-border/30 p-3 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/5",
        severityStyles[severity]
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className={cn("flex h-7 w-7 items-center justify-center rounded-full", severity === "severe" && "animate-pulse")}>
          <AlertTriangle className={cn("h-4 w-4", severity === "severe" ? "text-destructive" : severity === "moderate" ? "text-orange-400" : "text-amber-400")} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">
              {count} Active Alert{count > 1 ? "s" : ""}
            </span>
            <Badge
              variant={severity === "severe" ? "destructive" : "warning"}
              size="sm"
            >
              {severity}
            </Badge>
          </div>
          <p className="text-[11px] text-foreground/70 mt-0.5 line-clamp-1">{event}</p>
        </div>
        <ArrowRight className="h-3 w-3 text-muted-foreground/30 flex-shrink-0" />
      </div>
    </Link>
  );
}
